'use client';

import { usePulseTrackerOptional } from '@cineamo/pulse/react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { OrderStateEnum } from '@cineamo/api/models/order/OrderStateEnum.types';
import { SeatDto, SeatType } from '@cineamo/api/models/seat/SeatDto.types';
import { SeatCategoryDto } from '@cineamo/api/models/seat-category/SeatCategoryDto.types';
import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { toBackendLocale } from '@cineamo/ui/src/constants/locale-constants';

import { isReportableApiFailure } from '@cineamo/api/helpers/api-fallback';
import { apiResponseHandler } from '@cineamo/api/helpers/api-response-helper/api-response-helper';
import { getCartByUuid } from '@cineamo/api/resources/cart/cart-api';
import { getCartConcessions, setCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';
import { PaymentApiSessionResponse } from '@cineamo/api/resources/payment-api/CineamoPaymentApiResponse.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { getLocaleFromLanguage } from '@cineamo/ui/src/helpers/locale-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useConcessionCatalog } from '@cineamo/composables/src/hooks/useConcessionCatalog';
import { useMovie } from '@cineamo/composables/src/hooks/useMovie';
import useSessionStorage from '@cineamo/composables/src/hooks/useSessionStorage';
import useStripeUrlQueryParams from '@cineamo/composables/src/hooks/useStripeUrlQueryParams';
import { useTimeZone } from '@cineamo/ui/src/hooks/formatter/TimeZoneProvider';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';
import { useIsBelowLg } from '@cineamo/ui/src/hooks/useMediaQuery';

import Button from '@cineamo/ui/src/button/Button';
import { IconBack16 } from '@cineamo/ui/src/icons/ic-back';
import { IconPlus16 } from '@cineamo/ui/src/icons/ic-plus';
import { IconReset16 } from '@cineamo/ui/src/icons/ic-reset';
import type { RoomAmbience } from '@cineamo/ui/src/room-3d/ambience/ambience';
import { screenFilmFromMovie } from '@cineamo/ui/src/room-3d/screen-film-from-movie';
import type { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';
import WebsiteHeader from '@cineamo/ui/src/website/WebsiteHeader';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import {
    BookingStep,
    CartUuidStorageKey,
    type SelectedSeat,
    useCartProvider
} from '@cineamo/composables/src/providers/cartProvider';
import useUserStore from '@cineamo/composables/src/stores/useUserStore';
import CheckoutButton from '@cineamo/composables/src/ticket-booking/CheckoutButton';
import { showingDate } from '@cineamo/composables/src/ticket-booking/room-ambience';
import { CheckoutShowingInfoContext } from '@cineamo/composables/src/ticket-booking/showing-info-context';
import ShowingDetails from '@cineamo/composables/src/ticket-booking/ShowingDetails';

import { useSeatMap } from '../hooks/useSeatMap';

import type { BuyerDetails } from './actions';
import { BOOKING_QUERY_ROOTS } from './booking-query-keys';
import {
    paymentIntentIdOf,
    reportBookingError,
    setBookingContext,
    trackBookingEvent
} from './booking-telemetry-client';
import BookingFooter from './BookingFooter';
import CartSummary from './CartSummary';
import CheckoutForm, { storeBuyer } from './CheckoutForm';
import { isConcessionsWithoutTicketsError } from './concession-helper';
import LockCountdown from './countdown/LockCountdown';
import type { Wallet } from './ExpressCheckout';
import GiftCardRedemption from './GiftCardRedemption';
import PaymentFailedView from './PaymentFailedView';
import PaymentSucceededView from './PaymentSucceededView';
import {
    buildFunnelContext,
    buildPurchaseContext,
    clearPurchaseSnapshot,
    isConfirmedPurchase,
    markPurchaseTracked,
    readPurchaseSnapshot,
    resolvePurchaseContext,
    savePurchaseSnapshot,
    wasPurchaseTracked
} from './pulse-context';
import { writePendingRedirect } from './redirect-recheck';
import {
    categorySurcharge,
    defaultPricesByCategory,
    describeSeats,
    hasShowcase,
    pickSeatUpsell,
    roomSeatCategories,
    type SeatUpsell as SeatUpsellOffer
} from './seat-showcase';
import SeatCategoryShowcaseModal from './SeatCategoryShowcaseCard';
import { legendFurnitureOf } from './SeatMapLegend';
import SeatSelection from './SeatSelection';
import SeatUpsell, { SeatUpsellNotice } from './SeatUpsell';
import SnackSelectionModal from './SnackSelectionModal';
import { useErrorHandler } from './useErrorHandler';
import { useSeatsWithLockState } from './useSeatsWithLockState';

export type TicketBookingPageProps = {
    seatCategories: SeatCategoryDto[];
    showCinemaName?: boolean;
    websiteStyle: WebsiteStyle & WebsiteInputStyle;
    sidebarStyle: string;
    contentMaxHeight: string;
    /** The page's header has a back arrow on small screens that steps back through the checkout, so no step needs a link of its own there. */
    headerHasBackButton?: boolean;
    /**
     * The app's showing info, opened by "Details" on small screens; it reads its open state with
     * `useCheckoutShowingInfo`. Left out, "Details" links to the film's page.
     */
    showingInfoModal?: React.ReactNode;
    /** The cinema sells concessions online (`cinemaConfiguration.isConcessionSaleEnabled`). */
    isConcessionSaleEnabled?: boolean;
    /** Shows the gift card field in the cart step; off by default, as the cinema has to have redemption enabled. */
    isGiftCardRedemptionEnabled?: boolean;
    /**
     * The ticket system books a gift card alone and cannot take a payment for the rest (Cinetixx, CIN-15394):
     * a gift card that leaves something due blocks the checkout instead of failing after the customer paid.
     */
    mustGiftCardCoverCart?: boolean;
    /** Offers "Find best seats" over the seat map; on by default, the cinema can switch it off. */
    isBestSeatsEnabled?: boolean;
    /**
     * Shows the seat category showcase (priced legend, card, hint, cart lines); off by default, the cinema
     * switches it on (`cinemaConfiguration.isSeatCategoryShowcaseEnabled`).
     */
    isSeatCategoryShowcaseEnabled?: boolean;
    /** Suggests better free seats in the cart; off by default (`cinemaConfiguration.isSeatUpsellEnabled`). */
    isSeatUpsellEnabled?: boolean;
    /** Decorates the 3D room with the season's or the film's ambience; off by default (`cinemaConfiguration.isRoomAmbienceEnabled`). */
    isRoomAmbienceEnabled?: boolean;
    /** A ready-made ambience shown instead of the server's pick, `null` for none; given, nothing is fetched (room studio, demo). */
    ambienceOverride?: RoomAmbience | null;
    /** The cinema's IANA time zone (`Cinema.timezone`): the showing's date for the ambience lookup is read in it. */
    cinemaTimeZone?: string;
    /** Leaves the booking from seat picking; given, the back link there calls it instead of linking to the film's page. */
    onExit?: () => void;
    /** The label of that back link while `onExit` is given; the film page's label otherwise. */
    exitLabel?: string;
    /** Answers the checkout's "Pay" instead of creating a PaymentIntent from the cart; same contract as the built-in one. */
    requestPaymentIntent?: (buyer: BuyerDetails, onEmailRefused?: () => void) => Promise<string | 'paid' | null>;
};

const RESOLVE_ATTEMPTS = 3;
const RESOLVE_BACKOFF_MS = 1000;

/** The action answers null on any failure; a transient one is worth a second and third try before giving up. */
async function resolveWithRetry<T>(request: () => Promise<T | null>): Promise<T | null> {
    for (let attempt = 0; attempt < RESOLVE_ATTEMPTS; attempt++) {
        if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, RESOLVE_BACKOFF_MS * attempt));
        const result = await request().catch(() => null);
        if (result) return result;
    }
    return null;
}

const TicketBookingPage: React.FC<TicketBookingPageProps> = ({
    seatCategories,
    showCinemaName,
    websiteStyle,
    sidebarStyle,
    contentMaxHeight,
    headerHasBackButton,
    showingInfoModal,
    isConcessionSaleEnabled = false,
    isGiftCardRedemptionEnabled,
    mustGiftCardCoverCart = false,
    isBestSeatsEnabled = true,
    isSeatCategoryShowcaseEnabled = false,
    isSeatUpsellEnabled = false,
    isRoomAmbienceEnabled = false,
    ambienceOverride,
    cinemaTimeZone,
    onExit,
    exitLabel,
    requestPaymentIntent: requestPaymentIntentOverride
}) => {
    const {
        selectedSeats,
        setItems,
        currency,
        cartUuid,
        paidCartUuid,
        setPaidCartUuid,
        showing,
        items,
        isProcessing,
        total,
        concessions,
        setConcessions,
        refreshCartDto,
        reloadCart,
        unselectSeat,
        amountDue,
        currentStep,
        setCurrentStep,
        priceOptions,
        selectSeat,
        refetchSeatLocks,
        isCartLoadFailed,
        retryCartLoad,
        actions: { createPaymentIntent, syncSeatLocks, resolvePaymentIntentCart }
    } = useCartProvider();
    const { handleErrorResponse } = useErrorHandler();
    const { user } = useUserStore();
    const locale = useLocale();
    const [isPageHaveUnsavedChanges, setIsPageHaveUnsavedChanges] = useSessionStorage(
        'isPageHaveUnsavedChanges',
        'false'
    );

    const {
        seats,
        room,
        roomObjects,
        isLoading: isSeatMapLoading,
        isError: isSeatMapError,
        isRetrying: isSeatMapRetrying,
        refetch: refetchSeatMap
    } = useSeatMap(showing?.cinemaRoomId);
    const { movie } = useMovie(showing?._embedded?.content?.movieId, locale, showing?.cinemaId);
    const showingId = showing?.id;
    const showingCinemaId = showing?.cinemaId;
    const showingStart = showing?.startDatetime;
    const showingMovieId = showing?._embedded?.content?.movieId;
    const timeZone = useTimeZone();
    const ambience = useMemo(
        () => ({
            showingId,
            cinemaId: showingCinemaId,
            date: showingDate(showingStart, cinemaTimeZone ?? timeZone),
            movieId: showingMovieId,
            isEnabled: isRoomAmbienceEnabled,
            override: ambienceOverride
        }),
        [
            showingId,
            showingCinemaId,
            showingStart,
            showingMovieId,
            cinemaTimeZone,
            timeZone,
            isRoomAmbienceEnabled,
            ambienceOverride
        ]
    );
    const screenFilm = useMemo(() => (movie ? (screenFilmFromMovie(movie) ?? undefined) : undefined), [movie]);
    const { track } = useAnalytics();
    // Optional: a host without a PulseProvider (the room preview's booking demo) reports nothing.
    const trackPulse = usePulseTrackerOptional()?.track;

    const seatsInCart = useMemo(() => items.filter((item) => item._embedded?.product?.type === 'SeatLock'), [items]);
    const { paymentIntent, paymentIntentClientSecret, redirectStatus } = useStripeUrlQueryParams();

    const t = useTranslations('tickets');
    const { tabs: concessionTabs } = useConcessionCatalog(showing?.cinemaId ?? 0, isConcessionSaleEnabled);

    // One attempt per cart and snack set: a failed clear re-sets the snacks, which must not trigger the next attempt.
    const clearedSnacksKey = useRef<string | null>(null);
    useEffect(() => {
        // Snacks are only sold together with tickets (`concessions_without_tickets`).
        if (selectedSeats.length > 0) {
            clearedSnacksKey.current = null;
            return;
        }
        if (concessions.length === 0 || !cartUuid) return;
        const attemptKey = `${cartUuid}:${concessions.map(({ cinemaProductId }) => cinemaProductId).join(',')}`;
        if (clearedSnacksKey.current === attemptKey) return;
        clearedSnacksKey.current = attemptKey;
        setCartConcessions(
            cartUuid,
            concessions.flatMap(({ cinemaProductId }) =>
                cinemaProductId === null ? [] : [{ cinemaProductId, quantity: 0 }]
            )
        )
            .then(() => {
                clearedSnacksKey.current = null;
                setConcessions([]);
            })
            .catch(async (error: unknown) => {
                if (isReportableApiFailure(error)) {
                    reportBookingError({ step: 'concession-clear', code: 'failed', error, level: 'warning' });
                }
                // The server still holds the snacks: show what it holds instead of pretending they are gone.
                const remaining = await getCartConcessions(cartUuid).catch(() => null);
                if (remaining) setConcessions(remaining._embedded?.concessions ?? []);
            })
            .then(() => refreshCartDto());
    }, [selectedSeats.length, concessions, cartUuid, setConcessions, refreshCartDto]);

    const [snackSelectionModalIsOpen, setSnackSelectionModalIsOpen] = useState(false);
    const [isShowingInfoOpen, setIsShowingInfoOpen] = useState(false);
    const showingInfoState = useMemo(
        () => ({ isOpen: isShowingInfoOpen, close: () => setIsShowingInfoOpen(false) }),
        [isShowingInfoOpen]
    );
    const [isCheckoutButtonLoading, setIsCheckoutButtonLoading] = useState(false);
    const [paymentSubmitTrigger, setPaymentSubmitTrigger] = useState(0);
    // Why the guest is back on the checkout after a payment redirect; cleared by the next attempt.
    const [paymentNotice, setPaymentNotice] = useState<string>();
    // The paid cart could not be resolved after a redirect into a tab that never held it (CIN-15446).
    const [isPaidCartUnresolved, setIsPaidCartUnresolved] = useState(false);
    // Set when the cart drops a seat by its x: the step stays, the locks are already synced.
    const keepStepOnSeatChange = useRef(false);
    // The checkout's payment method ('wallet' or a Payment Element type); it decides the form and the footer's button.
    const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
    const [wallet, setWallet] = useState<Wallet | null>(null);
    const isBelowLg = useIsBelowLg();

    /**
     * Creates the PaymentIntent for the buyer: its client secret, 'paid' when nothing is due (a free showing, or gift
     * cards cover everything: the backend has already completed the order, CIN-15034), or null after an error it has
     * shown. A refused address goes to `onEmailRefused`, else to a toast.
     */
    const requestPaymentIntent = useCallback(
        async (buyer: BuyerDetails, onEmailRefused?: () => void): Promise<string | 'paid' | null> => {
            try {
                const res = await createPaymentIntent(
                    cartUuid,
                    buyer,
                    currency,
                    // An /en-US/ page would otherwise send en_US, which the API rejects (CIN-15225).
                    toBackendLocale(getLocaleFromLanguage(locale))
                );
                let result: string | 'paid' | null = null;
                apiResponseHandler<PaymentApiSessionResponse>(
                    res,
                    (successResponse) => {
                        result = 'client_secret' in successResponse ? successResponse.client_secret : 'paid';
                        const paymentIntentId = result === 'paid' ? undefined : paymentIntentIdOf(result);
                        if (paymentIntentId) setBookingContext({ payment_intent: paymentIntentId });
                        trackBookingEvent('booking_payment_intent_created', {
                            outcome: result === 'paid' ? 'paid' : 'client_secret',
                            amount_due: amountDue
                        });
                        if (showing?.cinemaId) {
                            savePurchaseSnapshot(
                                cartUuid,
                                buildPurchaseContext({
                                    showingId: showing.id,
                                    cinemaId: showing.cinemaId,
                                    seatQuantities: seatsInCart.map((item) => item.quantity),
                                    total,
                                    concessionQuantities: concessions.map((concession) => concession.quantity)
                                })
                            );
                        }
                    },
                    (errorResponse) => {
                        // The buyer's to fix are only counted (PostHog); anything else is ours and reported (Sentry too).
                        const isBuyerFixable =
                            'receiptEmail' in (errorResponse.data?.parameters ?? {}) ||
                            'giftCard' in (errorResponse.data?.parameters ?? {}) ||
                            isConcessionsWithoutTicketsError(errorResponse);
                        if (isBuyerFixable) {
                            trackBookingEvent('booking_error', {
                                step: 'payment-intent',
                                code: 'refused',
                                status: errorResponse.status
                            });
                        } else {
                            reportBookingError({
                                step: 'payment-intent',
                                code: errorResponse.status ? 'api-error' : 'no-response',
                                error: errorResponse,
                                level: errorResponse.status && errorResponse.status < 500 ? 'warning' : 'error'
                            });
                        }
                        // The address itself was refused, e.g. a domain that cannot receive mail: say so
                        // beside the field, where the buyer can fix it (CIN-15349).
                        if ('receiptEmail' in (errorResponse.data?.parameters ?? {})) {
                            if (onEmailRefused) {
                                onEmailRefused();
                                return;
                            }
                            showToast({
                                toastType: toast.info,
                                messageTitle: t('tickets.payment.email-input.email-undeliverable')
                            });
                            return;
                        }
                        // The backend refused a gift card that leaves something due at this ticket system (CIN-15387).
                        if ('giftCard' in (errorResponse.data?.parameters ?? {})) {
                            showToast({
                                toastType: toast.info,
                                messageTitle: t('gift-card.must-cover-cart')
                            });
                            return;
                        }
                        showToast({
                            toastType: toast.info,
                            // `detail` carries the reason ("Bitte wählen Sie die Plätze erneut aus. (C5200)");
                            // `title` is the generic problem type ("Invalid parameter") and says nothing.
                            messageTitle: isConcessionsWithoutTicketsError(errorResponse)
                                ? t('error.concessions-without-tickets')
                                : errorResponse.data?.detail ||
                                  errorResponse.data?.title ||
                                  t('error.something-went-wrong')
                        });
                    }
                );
                return result;
            } catch (error) {
                // The server action itself failed (not the API behind it): the buyer sees a toast, we must see this.
                reportBookingError({ step: 'payment-intent', code: 'action-failed', error });
                showToast({
                    toastType: toast.error,
                    messageTitle: t('error.something-went-wrong')
                });
                return null;
            }
        },
        [cartUuid, concessions, currency, locale, seatsInCart, showing, t, total, amountDue, createPaymentIntent]
    );

    const completePayment = useCallback(() => {
        void reloadCart();
        setPaidCartUuid(cartUuid);
        setCurrentStep('03_payment_complete');
    }, [cartUuid, reloadCart, setCurrentStep, setPaidCartUuid]);

    const onCheckoutButtonClick = useCallback(async () => {
        switch (currentStep) {
            case '01_seat_picking':
                {
                    setIsCheckoutButtonLoading(true);
                    const result = await syncSeatLocks(cartUuid, showing.id, selectedSeats, items);
                    apiResponseHandler(
                        result?.response ?? null,
                        async () => {
                            const cartItems = result.cartItems;
                            setItems(cartItems);
                            // The totals of step 01.5 and the payment are the API cart's, so read them once synced.
                            await refreshCartDto();
                            setCurrentStep('01.5_cart');
                        },
                        (errorResponse) => {
                            handleErrorResponse(errorResponse);
                        }
                    );
                    setIsCheckoutButtonLoading(false);
                }
                break;
            case '01.5_cart':
                // Each visit starts on the wallet, where there is one: express first.
                setSelectedMethod(wallet ? 'wallet' : null);
                setCurrentStep('02_payment');
                break;
            case '02_payment':
                setPaymentNotice(undefined);
                setPaymentSubmitTrigger((trigger) => trigger + 1);
                break;
        }
    }, [
        cartUuid,
        currentStep,
        handleErrorResponse,
        items,
        refreshCartDto,
        selectedSeats,
        setCurrentStep,
        setItems,
        showing.id,
        syncSeatLocks
    ]);

    /**
     * A seat's x. On seat picking it only unselects; on the cart the locks are synced without it and the cart
     * stays, instead of the seat-count change sending the guest back to the map. The last seat goes back there.
     */
    const removeSeat = useCallback(
        async (seatId: SeatDto['id']) => {
            const remaining = selectedSeats.filter((seat) => seat.id !== seatId);
            if (currentStep !== '01.5_cart' || remaining.length === 0) {
                unselectSeat(seatId);
                return;
            }
            setIsCheckoutButtonLoading(true);
            const result = await syncSeatLocks(cartUuid, showing.id, remaining, items);
            apiResponseHandler(
                result?.response ?? null,
                async () => {
                    keepStepOnSeatChange.current = true;
                    setItems(result.cartItems);
                    unselectSeat(seatId);
                    refetchSeatLocks();
                    await refreshCartDto();
                },
                (errorResponse) => handleErrorResponse(errorResponse)
            );
            setIsCheckoutButtonLoading(false);
        },
        [
            cartUuid,
            currentStep,
            handleErrorResponse,
            items,
            refetchSeatLocks,
            refreshCartDto,
            selectedSeats,
            setItems,
            showing.id,
            unselectSeat,
            syncSeatLocks
        ]
    );

    // The seat category whose showcase is open, and whether the cart's upsell was turned down: both for this visit.
    const [showcaseCategory, setShowcaseCategory] = useState<SeatCategory | null>(null);
    // A seat tap shows the compact chip; the chip and the legend's info button open the full card.
    const [isShowcaseOpen, setIsShowcaseOpen] = useState(false);
    // The showcase was opened from the swap notice's "Oder {name}" link: its swap is tracked with that source.
    const isOpenedFromNotice = useRef(false);
    const changeShowcase = useCallback((category: SeatCategory | null, isOpen = false) => {
        isOpenedFromNotice.current = false;
        setShowcaseCategory(category);
        setIsShowcaseOpen(!!category && isOpen);
    }, []);
    const [isUpsellDismissed, setIsUpsellDismissed] = useState(false);
    // Per showing, and here rather than in the steps, which remount between cart and payment.
    const offeredShowcases = useRef(new Set<string>());
    const shownUpsells = useRef(new Set<string>());
    const shouldAutoShowcase = useCallback(
        (category: SeatCategory) => {
            const key = `${showing.id}:${category.id}`;
            if (offeredShowcases.current.has(key)) return false;
            offeredShowcases.current.add(key);
            return true;
        },
        [showing.id]
    );
    const shouldTrackUpsellShown = useCallback(
        (categoryId: SeatCategory['id']) => {
            const key = `${showing.id}:${categoryId}`;
            if (shownUpsells.current.has(key)) return false;
            shownUpsells.current.add(key);
            return true;
        },
        [showing.id]
    );
    const seatsWithLockState = useSeatsWithLockState(seats);
    const formatter = useFormat();
    const showcase = useMemo(() => {
        const roomCategories = roomSeatCategories(seats);
        const prices = defaultPricesByCategory(priceOptions);
        return {
            categoryIds: new Set(roomCategories.filter(hasShowcase).map((category) => category.id)),
            onOpen: (category: SeatCategory) => changeShowcase(category, true),
            // What the guest pays on the default price option, as on the card and the cart line.
            surcharge: (category: SeatCategory) => {
                const amount = categorySurcharge(category, roomCategories, prices);
                return amount === undefined ? undefined : formatter.number.formatCurrency(amount / 100, currency);
            }
        };
    }, [seats, priceOptions, currency, formatter, changeShowcase]);
    // Off: the legend stays plain and nothing opens a card, so the showcase state stays null.
    const activeShowcase = isSeatCategoryShowcaseEnabled ? showcase : undefined;

    /** The cart's upsell: the whole selection moves to `picks`; the locks are synced first, so a taken seat changes nothing. */
    const swapSeats = useCallback(
        async (picks: Seat[]): Promise<boolean> => {
            let isSwapped = false;
            const next: SelectedSeat[] = picks.map((seat) => ({
                id: seat.id,
                categoryId: seat.category?.id,
                priceOptionId: priceOptions.find(
                    (option) => option._embedded?.seatCategory?.id === seat.category?.id && option.isShowingDefault
                )?.id
            }));
            setIsCheckoutButtonLoading(true);
            const result = await syncSeatLocks(cartUuid, showing.id, next, items);
            apiResponseHandler(
                result?.response ?? null,
                async () => {
                    keepStepOnSeatChange.current = true;
                    setItems(result.cartItems);
                    selectedSeats.forEach((seat) => unselectSeat(seat.id));
                    picks.forEach((seat) => selectSeat({ ...seat, isSelected: true }, { silent: true }));
                    isSwapped = true;
                    // The seats given up are released: without fresh locks they stay "taken" for this guest.
                    refetchSeatLocks();
                    await refreshCartDto();
                },
                (errorResponse) => {
                    // The group was taken meanwhile: without fresh locks the same one would be offered again.
                    refetchSeatLocks();
                    handleErrorResponse(errorResponse);
                }
            );
            setIsCheckoutButtonLoading(false);
            return isSwapped;
        },
        [
            cartUuid,
            handleErrorResponse,
            items,
            priceOptions,
            refetchSeatLocks,
            refreshCartDto,
            selectSeat,
            selectedSeats,
            setItems,
            showing.id,
            unselectSeat,
            syncSeatLocks
        ]
    );

    // The one swap that can still be undone: the seats it replaced (a second swap from the notice replaces this snapshot,
    // so "Undo" always goes back one swap only), and the selection it left, which any other change ends.
    const [swapNotice, setSwapNotice] = useState<{
        categoryId: SeatCategory['id'];
        name: string;
        total: string;
        previous: Seat[];
        selection: string;
        /** The swap seats a party together that was split before. */
        isTogether: boolean;
    } | null>(null);
    const selectionKey = (seatsOf: { id: SeatDto['id'] }[]) =>
        seatsOf
            .map((seat) => seat.id)
            .sort()
            .join();
    const currentSelection = selectionKey(selectedSeats);
    useEffect(() => {
        if (swapNotice && swapNotice.selection !== currentSelection) setSwapNotice(null);
    }, [swapNotice, currentSelection]);
    const onUpsellSwap = useCallback(
        async (offer: SeatUpsellOffer): Promise<boolean> => {
            const previous = seatsWithLockState.filter((seat) => seat.isSelected);
            if (!(await swapSeats(offer.seats))) return false;
            const isAdjacent = (list: Seat[]) => {
                const rows = describeSeats(list);
                return rows.length === 1 && rows[0].positions.includes('\u2013');
            };
            setSwapNotice({
                categoryId: offer.category.id,
                name: offer.category.name,
                total: formatter.number.formatCurrency((offer.priceDifference * offer.seats.length) / 100, currency),
                previous,
                selection: selectionKey(offer.seats),
                isTogether: previous.length > 1 && !isAdjacent(previous) && isAdjacent(offer.seats)
            });
            return true;
        },
        [seatsWithLockState, swapSeats, formatter, currency]
    );
    const upsellPricing = useMemo(() => {
        const prices = defaultPricesByCategory(priceOptions);
        return {
            defaultPrice: (categoryId: SeatCategory['id']) => prices.get(categoryId),
            selected: selectedSeats,
            defaultPriceOptionId: (categoryId: SeatCategory['id']) =>
                priceOptions.find(
                    (option) => option._embedded?.seatCategory?.id === categoryId && option.isShowingDefault
                )?.id
        };
    }, [priceOptions, selectedSeats]);
    // After a swap the notice offers the next step up from the CURRENT category, if a free block of the party fits.
    const noticeNext = useMemo(
        () => (swapNotice && isSeatUpsellEnabled ? pickSeatUpsell(seatsWithLockState, upsellPricing) : null),
        [swapNotice, isSeatUpsellEnabled, seatsWithLockState, upsellPricing]
    );
    // The showcase modal's upgrade: the same rules as the cart's offer, aimed at the category the guest is looking at.
    const modalUpgrade = useMemo(() => {
        if (
            !isSeatUpsellEnabled ||
            !showcaseCategory ||
            !isShowcaseOpen ||
            !['01_seat_picking', '01.5_cart'].includes(currentStep)
        ) {
            return null;
        }
        return pickSeatUpsell(seatsWithLockState, upsellPricing, showcaseCategory.id);
    }, [isSeatUpsellEnabled, showcaseCategory, isShowcaseOpen, currentStep, seatsWithLockState, upsellPricing]);
    const onModalUpgrade = useCallback(async () => {
        if (!modalUpgrade) return;
        track('Seat Upsell Accepted', {
            categoryId: modalUpgrade.category.id,
            priceDifference: modalUpgrade.priceDifference,
            source: isOpenedFromNotice.current ? 'notice' : 'modal'
        });
        if (currentStep === '01.5_cart') {
            if (!(await onUpsellSwap(modalUpgrade))) return;
        } else {
            // No locks yet on the map: the swap is a silent re-selection.
            selectedSeats.forEach((seat) => unselectSeat(seat.id));
            modalUpgrade.seats.forEach((seat) => selectSeat({ ...seat, isSelected: true }, { silent: true }));
        }
        changeShowcase(null);
    }, [modalUpgrade, currentStep, onUpsellSwap, selectedSeats, unselectSeat, selectSeat, track, changeShowcase]);
    const onUpsellUndo = useCallback(async () => {
        if (!swapNotice) return;
        track('Seat Upsell Reverted', { categoryId: swapNotice.categoryId });
        // Undoing a swap is a "no thanks": the same offer must not come back in the next breath.
        setIsUpsellDismissed(true);
        setSwapNotice(null);
        await swapSeats(swapNotice.previous);
    }, [swapNotice, swapSeats, track]);

    useLayoutEffect(() => {
        if (cartUuid === paidCartUuid) {
            setCurrentStep('03_payment_complete');
        }
    }, [cartUuid, paidCartUuid, setCurrentStep]);

    useEffect(() => {
        // A failed redirect goes back to the checkout instead (see below).
        if (paymentIntentClientSecret && redirectStatus !== 'failed') {
            setCurrentStep('03_payment_complete');
        }
    }, [paymentIntentClientSecret, redirectStatus, setCurrentStep]);

    useEffect(() => {
        // Back from a payment redirect the cart loads its seats late; that must not undo the result step below.
        if (redirectStatus) return;
        if (keepStepOnSeatChange.current) {
            keepStepOnSeatChange.current = false;
            return;
        }
        setCurrentStep('01_seat_picking');
    }, [selectedSeats.length, setCurrentStep, redirectStatus]);

    const previousStep = useRef<BookingStep>(currentStep);
    // Once per cart: going back to the seats and forward again is the same funnel step.
    const seatSelectTrackedFor = useRef<string | null>(null);
    const concessionViewTrackedFor = useRef<string | null>(null);
    const ticketQuantity = seatsInCart.reduce((sum, item) => sum + item.quantity, 0);
    useEffect(() => {
        if (
            !snackSelectionModalIsOpen ||
            !showing?.cinemaId ||
            !cartUuid ||
            concessionViewTrackedFor.current === cartUuid
        )
            return;
        concessionViewTrackedFor.current = cartUuid;
        trackPulse?.({
            movie_id: showing.movieId,
            event_type: 'concession_view',
            context: buildFunnelContext(showing.id, showing.cinemaId)
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snackSelectionModalIsOpen]);
    useEffect(() => {
        if (previousStep.current !== currentStep) {
            track('Step Changed', {
                previousStep: previousStep.current,
                currentStep: currentStep,
                cartTotal: total.toString(),
                cartItemsCount: items.length
            });
            if (
                previousStep.current === '01_seat_picking' &&
                currentStep === '01.5_cart' &&
                showing?.cinemaId &&
                cartUuid &&
                seatSelectTrackedFor.current !== cartUuid
            ) {
                seatSelectTrackedFor.current = cartUuid;
                trackPulse?.({
                    movie_id: showing.movieId,
                    event_type: 'seat_select',
                    context: buildFunnelContext(showing.id, showing.cinemaId, ticketQuantity)
                });
            }
            previousStep.current = currentStep;
        }

        // One value, set once: setting it twice in one run (seat picking with no seat chosen while the cart still
        // holds seat locks) flipped it between renders, and since the setter changes with the value the effect
        // looped. The rules are the old ones, the later one winning.
        let hasUnsavedChanges = currentStep === '01_seat_picking' ? selectedSeats.length > 0 : undefined;
        if (selectedSeats.length !== seatsInCart.length) hasUnsavedChanges = true;
        if (currentStep === '01.5_cart' || currentStep === '02_payment') hasUnsavedChanges = true;
        if (currentStep === '03_payment_complete' || currentStep === '03_payment_error') hasUnsavedChanges = false;
        if (hasUnsavedChanges !== undefined) {
            setIsPageHaveUnsavedChanges(hasUnsavedChanges ? 'true' : 'false');
        }
        // The pulse reads (showing, cartUuid, ticketQuantity) are only taken on the step change itself.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentStep,
        items.length,
        seatsInCart.length,
        selectedSeats.length,
        setIsPageHaveUnsavedChanges,
        total,
        track
    ]);

    useEffect(() => {
        // Wero (and other bank-backed methods) come back `pending` while the bank confirms; the success view follows
        // the order until it is paid or cancelled, so pending goes there too instead of back to the seat map.
        if (redirectStatus === 'succeeded' || redirectStatus === 'pending') {
            // The success view reads this cart's order; until the webhook marks it paid, nothing else points there.
            // Only the first cart seen for this payment counts: once the provider finds it paid it swaps in a fresh,
            // empty cart while `redirect_status` stays in the URL, also across a reload (CIN-15388).
            if (cartUuid && paymentIntent) {
                const redirectedCartKey = `${CartUuidStorageKey}-redirect-${paymentIntent}`;
                let redirectedCartUuid = cartUuid;
                let isKnown = false;
                try {
                    isKnown = sessionStorage.getItem(redirectedCartKey) !== null;
                    redirectedCartUuid = sessionStorage.getItem(redirectedCartKey) ?? cartUuid;
                    sessionStorage.setItem(redirectedCartKey, redirectedCartUuid);
                } catch {
                    // Storage blocked: fall back to the current cart.
                }
                setPaidCartUuid(redirectedCartUuid);
                // A tab that never held this cart (PayPal's app switch opens the return URL in a new one, CIN-15446)
                // has a fresh, empty cart: the PaymentIntent names the paid one.
                if (!isKnown && paymentIntentClientSecret) {
                    void resolveWithRetry(() => resolvePaymentIntentCart(paymentIntentClientSecret)).then(
                        (paidUuid) => {
                            if (!paidUuid) {
                                // The paid cart cannot be found: polling this tab's empty cart would never settle.
                                reportBookingError({ step: 'redirect', code: 'resolve-failed' });
                                setIsPaidCartUnresolved(true);
                                // Forget the cart: a reload resolves the PaymentIntent again instead of polling the
                                // empty cart of this tab as if it were the paid one.
                                try {
                                    sessionStorage.removeItem(redirectedCartKey);
                                } catch {
                                    // Storage blocked: nothing was stored either.
                                }
                                return;
                            }
                            if (paidUuid === redirectedCartUuid) return;
                            try {
                                sessionStorage.setItem(redirectedCartKey, paidUuid);
                            } catch {
                                // Storage blocked: this render still follows the paid cart.
                            }
                            setPaidCartUuid(paidUuid);
                        }
                    );
                }
            }
            storeBuyer(null);
            setCurrentStep('03_payment_complete');
        }
        // Cancelled at PayPal, refused by the bank...: the seats are still held, so back to the checkout to try
        // again or pick another method, not to a dead end that throws the seats away. Once the cart has loaded them;
        // with none left (the hold ran out) the guest picks seats again.
        if (redirectStatus === 'failed' && selectedSeats.length > 0) {
            setPaymentNotice(t('checkout.payment-failed-retry'));
            setCurrentStep('02_payment');
        }
    }, [
        redirectStatus,
        paymentIntent,
        paymentIntentClientSecret,
        cartUuid,
        selectedSeats.length,
        setCurrentStep,
        setPaidCartUuid,
        t
    ]);

    // The page this payment redirected back to (PayPal, Wero, 3DS...): its status, once per payment intent and tab.
    const reportedRedirect = useRef<string | null>(null);
    useEffect(() => {
        if (!redirectStatus || reportedRedirect.current === `${paymentIntent}:${redirectStatus}`) return;
        reportedRedirect.current = `${paymentIntent}:${redirectStatus}`;
        if (paymentIntent) setBookingContext({ payment_intent: paymentIntent });
        // Back through the return URL: the checkout has nothing left to recheck (see CheckoutForm).
        try {
            writePendingRedirect(window.sessionStorage, null);
        } catch {
            // Storage blocked: nothing was stored either.
        }
        trackBookingEvent('booking_redirect_returned', { status: redirectStatus, payment_intent: paymentIntent });
        // The confirmation of a redirecting method ends on this page, not in `stripe.confirmPayment`, which never
        // answers the tab it left: the funnel's confirm step is counted here for those (CIN-15446).
        trackBookingEvent('booking_payment_confirmed', {
            outcome: redirectStatus === 'failed' ? 'error' : redirectStatus === 'pending' ? 'pending' : 'authorized',
            method: 'redirect',
            via: 'redirect'
        });
        if (redirectStatus === 'failed') {
            reportBookingError({ step: 'redirect', code: 'failed', level: 'warning' });
        }
    }, [redirectStatus, paymentIntent]);

    // A `pending` redirect waits for its order. Same key and fetch as PaymentSucceededView, whose polling drives
    // this observer too; a second interval here would only double the requests.
    const isPendingRedirect = redirectStatus === 'pending';
    const { data: redirectedCart } = useQuery({
        queryKey: [BOOKING_QUERY_ROOTS.paidCart, paidCartUuid],
        queryFn: async () => await getCartByUuid(paidCartUuid),
        // Same query as PaymentSucceededView's, which reports its failures itself.
        meta: { reportsOwnErrors: true },
        enabled: isPendingRedirect && !!paidCartUuid
    });

    // Pulse ticket_purchase on confirmed payment only (see isConfirmedPurchase): reaching the
    // 03_payment_complete step alone is not enough — it is also entered while the payment
    // intent is still open, and while a bank-backed payment is still `pending`.
    const pulsePurchaseTrackedRef = useRef(false);
    useEffect(() => {
        if (pulsePurchaseTrackedRef.current || !cartUuid || !showing?.movieId || !showing.cinemaId) return;
        const isRedirectPurchase = redirectStatus === 'succeeded' || isPendingRedirect;
        // After a redirect the current cart is the empty replacement; the paid one is what the redirect handler kept.
        let paidUuid = cartUuid;
        if (isRedirectPurchase && paymentIntent) {
            try {
                paidUuid = sessionStorage.getItem(`${CartUuidStorageKey}-redirect-${paymentIntent}`) ?? cartUuid;
            } catch {
                // Storage blocked: the current cart is all there is.
            }
        }
        if (
            !isConfirmedPurchase({
                redirectStatus,
                isPaidInSession: cartUuid === paidCartUuid,
                // The uuid check matters on the first render: paidCartUuid may still name an earlier purchase,
                // whose fulfilled order is already cached, until the redirect handler's update lands.
                isRedirectedOrderFulfilled:
                    redirectedCart?.uuid === paidUuid && redirectedCart?.orderState === OrderStateEnum.FULFILLED
            })
        )
            return;
        // The payment intent identifies the purchase across reloads of the redirect URL; in-session it is the cart.
        const trackedId = paymentIntent ?? cartUuid;
        if (wasPurchaseTracked(trackedId)) {
            pulsePurchaseTrackedRef.current = true;
            return;
        }
        pulsePurchaseTrackedRef.current = true;
        trackPulse?.({
            movie_id: showing.movieId,
            event_type: 'ticket_purchase',
            context: {
                ...resolvePurchaseContext(
                    showing.id,
                    showing.cinemaId,
                    paidUuid === cartUuid && ticketQuantity > 0
                        ? {
                              showingId: showing.id,
                              cinemaId: showing.cinemaId,
                              seatQuantities: seatsInCart.map((item) => item.quantity),
                              total,
                              concessionQuantities: concessions.map((concession) => concession.quantity)
                          }
                        : null,
                    readPurchaseSnapshot(paidUuid)
                ),
                // Not paidCartUuid: on the redirect it still holds the previous order until the effect above lands.
                // Read by nothing and never joined to an order: docs/adr/0040.
                cart_uuid: cartUuid
            }
        });
        markPurchaseTracked(trackedId);
        clearPurchaseSnapshot(paidUuid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectStatus, paymentIntent, cartUuid, paidCartUuid, showing, redirectedCart]);

    const showingDetails = useMemo(() => {
        return (
            <>
                <ShowingDetails
                    showing={showing}
                    movie={movie ?? undefined}
                    showCinemaName={showCinemaName}
                    cinemaRoom={room?.dto ?? undefined}
                />
                <LockCountdown />
            </>
        );
    }, [showing, movie, showCinemaName, room?.dto]);

    const onPageUnload = useCallback((e: BeforeUnloadEvent) => {
        // Read at the event: the header's back arrow clears it once the guest confirmed leaving in its modal.
        if (sessionStorage.getItem('isPageHaveUnsavedChanges') === 'true') {
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        if (isPageHaveUnsavedChanges) {
            window.addEventListener('beforeunload', onPageUnload);
        }
        if (!isPageHaveUnsavedChanges) {
            window.removeEventListener('beforeunload', onPageUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onPageUnload);
        };
    }, [onPageUnload, isPageHaveUnsavedChanges]);

    if (!showing) {
        return (
            <CenterLayout className="mt-32">
                <WebsiteHeader title="Showing not found!" />
            </CenterLayout>
        );
    }

    // One instance: its Stripe Elements must not mount twice. Wide screens show it beside the cart, small ones below.
    const checkoutForm = (
        <CheckoutForm
            amount={amountDue}
            currency={currency}
            websiteStyle={websiteStyle}
            submitTrigger={paymentSubmitTrigger}
            defaultBuyer={{ email: user?.email, firstName: user?.firstName, lastName: user?.lastName }}
            requestPaymentIntent={requestPaymentIntentOverride ?? requestPaymentIntent}
            onPaymentComplete={completePayment}
            selectedMethod={selectedMethod}
            onSelectMethod={setSelectedMethod}
            notice={paymentNotice}
            wallet={wallet}
        />
    );

    let currentStepView: React.JSX.Element | null;
    switch (currentStep) {
        case '01_seat_picking':
            currentStepView = (
                <SeatSelection
                    className="w-full"
                    isLoading={isSeatMapLoading}
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                    ambience={ambience}
                    isBestSeatsEnabled={isBestSeatsEnabled}
                    onShowcaseChange={isSeatCategoryShowcaseEnabled ? changeShowcase : undefined}
                    shouldAutoShowcase={isSeatCategoryShowcaseEnabled ? shouldAutoShowcase : undefined}
                />
            );
            break;
        case '01.5_cart':
            currentStepView = (
                <SeatSelection
                    className="hidden lg:block w-full"
                    room={room}
                    seats={seats}
                    roomObjects={roomObjects}
                    seatCategories={seatCategories}
                    selectedSeatColor={websiteStyle.primaryColor}
                    screenFilm={screenFilm}
                    // display:none on phones: it would fetch and build an ambience nobody sees.
                    ambience={isBelowLg ? undefined : ambience}
                    isBestSeatsEnabled={isBestSeatsEnabled}
                    onShowcaseChange={isSeatCategoryShowcaseEnabled ? changeShowcase : undefined}
                    shouldAutoShowcase={isSeatCategoryShowcaseEnabled ? shouldAutoShowcase : undefined}
                />
            );
            break;
        case '02_payment':
            // Small screens put the form in the sidebar's column, below the film; see `checkoutForm`.
            currentStepView = isBelowLg ? null : <div className="lg:h-full lg:overflow-y-auto">{checkoutForm}</div>;
            break;
        case '03_payment_complete':
            currentStepView = (
                <PaymentSucceededView
                    isPaidCartUnresolved={isPaidCartUnresolved}
                    paymentIntentId={paymentIntent}
                />
            );
            break;
        case '03_payment_error':
            currentStepView = <PaymentFailedView />;
            break;
    }
    // Not over the page after "Pay": back from a payment redirect, that page follows the paid cart by itself, and a
    // guest whose money is held must see the booking's state, not a load error for the cart left behind.
    if (isCartLoadFailed && currentStep !== '03_payment_complete') {
        currentStepView = (
            <div
                role="alert"
                className="flex flex-col items-center space-y-16 container max-w-[600px] mx-auto pt-[48px] px-16">
                <h2 className="text-headline-24 text-center">{t('booking-status.cart-load-failed-title')}</h2>
                <p className="text-body-14 whitespace-pre-wrap text-center">
                    {t('booking-status.cart-load-failed-message')}
                </p>
                <Button
                    leadingIcon={<IconReset16 />}
                    onClick={retryCartLoad}>
                    {t('booking-status.try-again')}
                </Button>
            </div>
        );
    }

    if (isSeatMapError && currentStep === '01_seat_picking') {
        currentStepView = (
            <div
                role="alert"
                className="flex flex-col items-center space-y-16 container max-w-[600px] mx-auto pt-[48px] px-16">
                <h2 className="text-headline-24 text-center">{t('booking-status.seat-map-load-failed-title')}</h2>
                <p className="text-body-14 whitespace-pre-wrap text-center">
                    {t('booking-status.seat-map-load-failed-message')}
                </p>
                <Button
                    leadingIcon={<IconReset16 />}
                    disabled={isSeatMapRetrying}
                    onClick={refetchSeatMap}>
                    {t('booking-status.try-again')}
                </Button>
            </div>
        );
    }

    // Steps go back by button, not `<Link href="">`: no navigation, and an empty href drops the URL's hash.
    let backButton: React.JSX.Element | undefined;
    switch (currentStep) {
        case '01_seat_picking':
            backButton = onExit ? (
                <button
                    type="button"
                    onClick={onExit}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {exitLabel ?? t('other-screenings')}
                </button>
            ) : (
                <Link
                    href={`/program/${showing._embedded?.content?.id}`}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('other-screenings')}
                </Link>
            );
            break;
        case '01.5_cart':
            backButton = (
                <button
                    type="button"
                    onClick={() => setCurrentStep('01_seat_picking')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-seat-selection')}
                </button>
            );
            break;
        case '02_payment':
            backButton = (
                <button
                    type="button"
                    onClick={() => setCurrentStep('01.5_cart')}
                    className="lg:px-12 lg:py-6 py-8 opacity-50 flex row items-center gap-8">
                    <IconBack16 /> {t('back-to-shopping-cart')}
                </button>
            );
            break;
        case '03_payment_complete':
        case '03_payment_error':
            break;
    }

    const isSidebarVisible = !isCartLoadFailed && !['03_payment_complete', '03_payment_error'].includes(currentStep);
    const hasTables = roomObjects.some((roomObject) => roomObject.type === 'table');
    const hasWheelchairSeats = seats.some((seat) => [SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type));
    const furniture = legendFurnitureOf(seats);

    return (
        <div className="relative flex flex-col lg:grow items-stretch">
            {isSeatCategoryShowcaseEnabled && showcaseCategory && isShowcaseOpen && (
                <SeatCategoryShowcaseModal
                    category={showcaseCategory}
                    seats={seats}
                    seatCategories={seatCategories}
                    onClose={() => changeShowcase(null)}
                    upgrade={
                        modalUpgrade
                            ? {
                                  total: formatter.number.formatCurrency(
                                      (modalUpgrade.priceDifference * modalUpgrade.seats.length) / 100,
                                      currency
                                  ),
                                  perSeat:
                                      modalUpgrade.seats.length > 1
                                          ? formatter.number.formatCurrency(
                                                modalUpgrade.priceDifference / 100,
                                                currency
                                            )
                                          : undefined,
                                  isBusy: isCheckoutButtonLoading || isProcessing,
                                  onAccept: onModalUpgrade
                              }
                            : undefined
                    }
                />
            )}
            <div className="flex flex-col lg:flex-row lg:grow items-stretch">
                {snackSelectionModalIsOpen && (
                    <SnackSelectionModal
                        modalIsOpen={snackSelectionModalIsOpen}
                        setModalIsOpen={(value) => setSnackSelectionModalIsOpen(value)}
                        onClose={() => setSnackSelectionModalIsOpen(false)}
                        tabs={concessionTabs}
                    />
                )}
                {showingInfoModal && (
                    <CheckoutShowingInfoContext.Provider value={showingInfoState}>
                        {showingInfoModal}
                    </CheckoutShowingInfoContext.Provider>
                )}
                <div
                    className={cn(
                        'grow flex flex-col space-y-[12px] overflow-y-auto lg:overflow-y-hidden text-[var(--on-background-color)]',
                        contentMaxHeight,
                        sidebarStyle
                    )}>
                    {backButton && (
                        <div
                            className={cn(
                                'lg:py-4 ml-[9px] lg:ml-32 shrink-0',
                                headerHasBackButton &&
                                    ['01_seat_picking', '01.5_cart', '02_payment'].includes(currentStep) &&
                                    'hidden lg:block'
                            )}>
                            {backButton}
                        </div>
                    )}
                    <div className="grow flex flex-col min-h-0">
                        {isSidebarVisible && (
                            <div className="lg:hidden flex flex-col">
                                <ShowingDetails
                                    showing={showing}
                                    movie={movie ?? undefined}
                                    compactDetailsHref={`/program/${showing._embedded?.content?.id}`}
                                    onShowCompactDetails={
                                        showingInfoModal ? () => setIsShowingInfoOpen(true) : undefined
                                    }
                                />
                                <div className="flex flex-col mx-[9px]">
                                    <LockCountdown />
                                </div>
                            </div>
                        )}
                        <div className="w-full mx-auto lg:overflow-x-hidden space-y-16 lg:relative lg:h-0 lg:grow lg:min-h-0">
                            {currentStepView}
                        </div>
                        <div className="grow lg:hidden"></div>
                        <BookingFooter
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                            showcase={activeShowcase}
                        />
                    </div>
                </div>

                {isSidebarVisible && (
                    <div
                        className={cn(
                            'relative lg:p-0 flex flex-col lg:w-[390px] lg:shrink-0 overflow-y-auto pb-[var(--booking-footer-height,169px)] lg:pb-0',
                            contentMaxHeight
                        )}>
                        <div className="hidden lg:flex flex-col">{showingDetails}</div>

                        {seats.length > 0 && (
                            // Small screens show the list in the footer's details while seats are picked and paid for.
                            <CartSummary
                                seats={seats}
                                onRemoveSeat={currentStep === '02_payment' ? undefined : removeSeat}
                                onShowCategory={activeShowcase?.onOpen}
                                // 12px below the reservation banner, per Figma (Cinema Websites 503:89271).
                                className={currentStep === '01.5_cart' ? 'pt-12 lg:pt-0' : 'hidden lg:block'}
                            />
                        )}

                        {isSeatUpsellEnabled && currentStep === '01.5_cart' && swapNotice && (
                            <SeatUpsellNotice
                                name={swapNotice.name}
                                total={swapNotice.total}
                                isTogether={swapNotice.isTogether}
                                isBusy={isCheckoutButtonLoading || isProcessing}
                                onUndo={onUpsellUndo}
                                next={
                                    noticeNext && activeShowcase
                                        ? {
                                              name: noticeNext.category.name,
                                              price: formatter.number.formatCurrency(
                                                  (noticeNext.priceDifference * noticeNext.seats.length) / 100,
                                                  currency
                                              ),
                                              onOpen: () => {
                                                  activeShowcase.onOpen(noticeNext.category);
                                                  isOpenedFromNotice.current = true;
                                              }
                                          }
                                        : undefined
                                }
                                className="mx-[9px] mt-16 lg:mx-16"
                            />
                        )}

                        {isSeatUpsellEnabled && currentStep === '01.5_cart' && !swapNotice && (
                            <SeatUpsell
                                isDeclined={isUpsellDismissed}
                                seats={seatsWithLockState}
                                seatCategories={seatCategories}
                                shouldTrackShown={shouldTrackUpsellShown}
                                currency={currency}
                                isBusy={isCheckoutButtonLoading || isProcessing}
                                onSwap={onUpsellSwap}
                                onShowCategory={activeShowcase?.onOpen}
                                onDismiss={() => setIsUpsellDismissed(true)}
                                className="mx-[9px] mt-16 lg:mx-16 [@media(max-height:799px)]:lg:mt-0"
                            />
                        )}

                        {isGiftCardRedemptionEnabled && currentStep === '01.5_cart' && (
                            <GiftCardRedemption
                                className="mx-[9px] mt-16 lg:mx-16"
                                websiteStyle={websiteStyle}
                                mustGiftCardCoverCart={mustGiftCardCoverCart}
                            />
                        )}

                        <div className="grow" />

                        {isConcessionSaleEnabled &&
                            concessionTabs.length > 0 &&
                            selectedSeats.length > 0 &&
                            currentStep === '01.5_cart' && (
                                <button
                                    type="button"
                                    onClick={() => setSnackSelectionModalIsOpen(true)}
                                    className={cn(
                                        'mx-[9px] lg:mx-16 my-16 flex min-h-48 items-center justify-center gap-8 rounded-4 border-2 px-16 text-button-16 outline-none',
                                        'border-[var(--primary-color)] text-[var(--primary-color)]',
                                        'focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2'
                                    )}>
                                    <IconPlus16 />
                                    {t('add-snacks')}
                                </button>
                            )}

                        {isBelowLg && currentStep === '02_payment' && checkoutForm}

                        <CheckoutButton
                            onCheckoutClick={onCheckoutButtonClick}
                            seats={seats}
                            seatCategories={seatCategories}
                            hasTables={hasTables}
                            hasWheelchairSeats={hasWheelchairSeats}
                            furniture={furniture}
                            showcase={activeShowcase}
                            showcaseHint={
                                isSeatCategoryShowcaseEnabled && showcaseCategory && !isShowcaseOpen
                                    ? {
                                          category: showcaseCategory,
                                          price: showcase.surcharge(showcaseCategory),
                                          onOpen: () => changeShowcase(showcaseCategory, true)
                                      }
                                    : undefined
                            }
                            isLoading={isCheckoutButtonLoading || isProcessing}
                            onRemoveSeat={currentStep === '01_seat_picking' ? removeSeat : undefined}
                            mustGiftCardCoverCart={mustGiftCardCoverCart}
                            express={{
                                requestPaymentIntent: requestPaymentIntentOverride ?? requestPaymentIntent,
                                onPaymentComplete: completePayment,
                                selectedMethod,
                                onWalletChange: (next) => {
                                    setWallet(next);
                                    // The wallet leads unless a method was chosen before it was found.
                                    if (next) setSelectedMethod((method) => method ?? 'wallet');
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TicketBookingPage;
